import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import React from 'react';

type ToolLink = {
  name: string;
  description: string;
  icon: string;
  href?: string;
};

// Keeps the internal links to every talking-video sub-page (SEO) in the
// compact dark card style shared with the homepage ToolsGrid.
const TOOLS: ToolLink[] = [
  {
    name: 'AI Video Upscaler',
    description: 'Give existing footage a sharper finish, up to 4K',
    icon: '↗',
    href: '/ai-video-upscaler',
  },
  {
    name: 'AI Video Extender',
    description: 'Continue the scene with new AI-generated footage',
    icon: '→',
    href: '/ai-video-extender',
  },
  {
    name: 'Edit Video with AI',
    description:
      'Replace, remove or change anything in a clip by describing it',
    icon: '✎',
    href: '/edit-video-with-ai',
  },
  {
    name: 'AI Talking Videos',
    description: 'Make any portrait speak with realistic lip-sync',
    icon: '🗣️',
    href: '/ai-video-generator/talking',
  },
  {
    name: 'Image to Video Conversion',
    description: 'Animate any image into lifelike motion',
    icon: '🎬',
    href: '/ai-animate-image',
  },
  {
    name: 'AI Talking Head Videos',
    description: 'Presenters for YouTube, courses and news',
    icon: '🎙️',
    href: '/ai-video-generator/talking-head',
  },
  {
    name: 'AI Spokesperson',
    description: 'On-brand spokesperson videos without actors',
    icon: '👔',
    href: '/ai-video-generator/spokesperson',
  },
  {
    name: 'Make Image Talk',
    description: 'Turn any photo into a talking video',
    icon: '🖼️',
    href: '/ai-video-generator/make-image-talk',
  },
  {
    name: 'AI Talking Avatar',
    description: 'A consistent digital persona, 100+ avatars',
    icon: '👤',
    href: '/ai-video-generator/talking-avatar',
  },
  {
    name: 'Seedance 2.5 Studio',
    description: 'Unrestricted 30-second takes with native audio',
    icon: '🎬',
    href: '/ai-video-generator/seedance-2-5',
  },
  {
    name: 'Kling 4.0 Studio',
    description: '30-second takes, 10 keyframes and stereo sound',
    icon: '🎭',
    href: '/ai-video-generator/kling-4-0',
  },
  {
    name: 'MiniMax H3 Studio',
    description: 'Unrestricted 2K video with native stereo sound',
    icon: '🔊',
    href: '/ai-video-generator/minimax-h3',
  },
  {
    name: 'WAN 3.0 Studio',
    description: '30-second 1080P stories with native audio',
    icon: '🎞️',
    href: '/ai-video-generator/wan-3-0',
  },
  {
    name: 'Gemini Omni 1.1 Studio',
    description: 'Generate, edit, interpolate and extend up to 4K',
    icon: '✦',
    href: '/ai-video-generator/gemini-omni-1-1-flash',
  },
  {
    name: 'Seedance 2.0 Studio',
    description: 'Native audio, physics and camera control',
    icon: '🎥',
    href: '/ai-video-generator/seedance-2',
  },
  {
    name: 'The Video Agent',
    description: 'Full videos with script, voice and music',
    icon: '🪄',
    href: '/create-video',
  },
];

const VideoToolLinks = () => {
  const { t } = useLocale('ai-videos');

  return (
    <section className='relative py-20 lg:py-28'>
      <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        <div className='mb-12 text-center'>
          <h2 className='text-4xl font-extrabold tracking-tight text-white md:text-5xl'>
            {t('A studio for every kind of video')}
          </h2>
          <p className='mx-auto mt-4 max-w-2xl text-lg text-slate-400'>
            {t('Purpose-built tools that share one subscription.')}
          </p>
        </div>
        <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4 lg:grid-cols-4'>
          {TOOLS.map((tool) => (
            <Link
              key={tool.name}
              href={tool.href || '/ai-videos'}
              className='group relative flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-5 transition-all hover:border-indigo-400/40 hover:bg-white/[0.07]'
            >
              <span className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-500/15 text-xl ring-1 ring-inset ring-indigo-400/20'>
                {tool.icon}
              </span>
              <span className='min-w-0'>
                <span className='block font-bold text-white'>
                  {t(tool.name)}
                </span>
                <span className='mt-0.5 block text-sm leading-snug text-slate-400'>
                  {t(tool.description)}
                </span>
              </span>
              <span
                aria-hidden='true'
                className='absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 opacity-0 transition-all group-hover:translate-x-0.5 group-hover:text-indigo-300 group-hover:opacity-100'
              >
                →
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default VideoToolLinks;
